import { captureException } from '@sentry/react';
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { z } from 'zod';

import { useRefCallback } from './use-ref-callback.ts';

type SetValue<T> = (value: T | ((prev: T) => T)) => void;

type UseLocalStorageOptions<T> = {
    schema?: z.ZodType<T>;
};

export function useLocalStorage<T>(
    key: string,
    initialValue: T,
    options?: UseLocalStorageOptions<T>,
): [T, SetValue<T>] {
    const getLocalStorageValue = useRefCallback(() => {
        try {
            const item = localStorage.getItem(key);
            if (item !== null) {
                const parsed: unknown = JSON.parse(item);
                return options?.schema ? options.schema.parse(parsed) : (parsed as T);
            }
        } catch (error) {
            captureException(
                new Error(`Error reading localStorage key "${key}"`, {
                    cause: error,
                }),
            );
        }
        return initialValue;
    });

    const [storedValue, setStoredValue] = useState<T>(() => getLocalStorageValue());

    useEffect(() => {
        // eslint-disable-next-line react-hooks/set-state-in-effect -- legacy code
        setStoredValue(getLocalStorageValue());

        const handleStorage = (e: StorageEvent): void => {
            if (e.key === key) {
                setStoredValue(getLocalStorageValue());
            }
        };

        window.addEventListener('storage', handleStorage);

        return () => {
            window.removeEventListener('storage', handleStorage);
        };
    }, [getLocalStorageValue, key]);

    const ref = useRef(storedValue);

    useLayoutEffect(() => {
        ref.current = storedValue;
    }, [storedValue]);

    const setValue: SetValue<T> = useCallback(
        (value) => {
            try {
                const valueToStore =
                    typeof value === 'function' ? (value as (prev: T) => T)(ref.current) : value;
                setStoredValue(valueToStore);
                localStorage.setItem(key, JSON.stringify(valueToStore));
                window.dispatchEvent(new StorageEvent('storage', { key }));
            } catch (error) {
                captureException(
                    new Error(`Error writing value to localStorage key "${key}"`, {
                        cause: error,
                    }),
                );
            }
        },
        [key],
    );

    return [storedValue, setValue];
}
